import { Action, createReducer, on } from '@ngrx/store';

import { Headquarter } from 'src/app/model/headquarter';
import { Lang, langFromLanguage } from 'src/app/model/lang';
import { User, currentRole } from 'src/app/model/user';
import * as UserActions from './user.actions';
import * as AuthActions from '../auth/auth.actions';
import { ItemsPagination } from 'src/app/model/api/data-rest';
import { LOCALSTORAGE_PERSONIFIED_USER, LOCALSTORAGE_USER_DATA } from 'src/app/conf/user';
import { NotificationMaintenance } from 'src/app/model/notification-maintenance';
import { ReleaseNote } from 'src/app/model/release-note';


export interface State {
    currentLang: Lang;
    currentUser?: User;
    isUser: boolean;
    isTechnician: boolean;
    isService: boolean;
    isAdmin: boolean;
    isMarketing: boolean;
    technicians: User[];
    customers: User[];
    allCustomers: User[];
    customerPaging?: ItemsPagination;
    customersLoading: boolean;
    noTechnicians: boolean;
    currentTechnician?: User;
    headquarters?: Headquarter[];
    technician?: User;
    userTechnicianAssigned: boolean;
    websocketMuted: boolean;
    isDraftMode: boolean;
    isImpersMode: boolean;
    customerInviting: boolean;
    customerInvited: boolean;
    deviceShareInvitationRevoked: boolean;
    accountClosed: boolean;
    exitBeta: boolean;
    notificationMaintenance?: NotificationMaintenance[];
    readyToImpers: boolean;
    searchFailed: boolean;
    releaseNoteList: ReleaseNote[];
    privacyNoteList: ReleaseNote[];
    ratingLoading: boolean;
    ratingSuccess: boolean;
    askNextRatingDate: string;
}
export const initialState: State = {
    currentLang: langFromLanguage(),
    technicians: [],
    customers: [],
    allCustomers: [],
    customersLoading: false,
    noTechnicians: true,
    userTechnicianAssigned: false,
    websocketMuted: true,
    isUser: false,
    isTechnician: false,
    isService: false,
    isAdmin: false,
    isMarketing: false,
    isDraftMode: false,
    isImpersMode: false,
    customerInviting: false,
    customerInvited: false,
    deviceShareInvitationRevoked: false,
    accountClosed: false,
    readyToImpers: false,
    searchFailed: true,
    exitBeta: false,
    releaseNoteList: [],
    privacyNoteList: [],
    ratingLoading: false,
    ratingSuccess: false,
    askNextRatingDate: '',
};

const userReducer = createReducer(
    initialState,
    on(UserActions.unmuteWebsocket, (state) => ({ ...state, websocketMuted: false })),
    on(UserActions.muteWebsocket, (state) => ({ ...state, websocketMuted: true })),
    on(UserActions.getCurrentUserSuccess, (state, payload) => {
        const role = currentRole(payload.currentUser);
        return {
            ...state,
            currentUser: payload.currentUser,
            currentTechnician: payload.currentTechnician,
            isImpersMode: !!payload.currentTechnician,
            ...role
        };
    }),
    //on(UserActions.setCurrentLang, (state, payload) => ({ ...state, currentLang: payload.lang })),
    on(UserActions.setCurrentLangSuccess, (state, payload) => ({ ...state, currentLang: payload.lang })),
    on(UserActions.getHeadQuartersSuccess, (state, payload) => ({ ...state, headquarters: payload.headquarters })),
    on(UserActions.getTechnicians, (state) => ({ ...state, noTechnicians: false })),
    on(UserActions.getTechniciansSuccess, (state, payload) => ({
        ...state,
        technicians: payload.technicians,
        noTechnicians: payload.technicians.length === 0
    })),
    on(UserActions.getTechnicianSuccess, (state, payload) => {
        const assigned = state.technicians.find(t => t.Id === payload.technician.Id);
        const technician = { ...payload.technician, AssignedTechnician: !!assigned };
        return { ...state, technician, searchFailed: false };
    }),
    on(UserActions.getTechnicianFailed, (state) => {
        return { ...state, technician: null, searchFailed: true };
    }),
    on(UserActions.assignTechnicianToUser, (state) => ({ ...state, userTechnicianAssigned: false })),
    on(UserActions.assignTechnicianToUserSuccess, (state) => ({ ...state, userTechnicianAssigned: true })),
    on(UserActions.unassignTechnicianToUserSuccess, (state, payload) => ({
        ...state, userTechnicianAssigned: false,
        technicians: state.technicians.filter(t => t.Id !== payload.id),
    })),
    on(AuthActions.getTokenSuccess, (state, payload) => {
        const currentUser = new User();
        currentUser.Id = payload.userId;
        return { ...state, currentUser };
    }),
    on(AuthActions.getMfaTokenSuccess, (state, payload) => {
        const currentUser = new User();
        currentUser.Id = payload.userId;
        return { ...state, currentUser };
    }),
    on(UserActions.getCustomers, (state) => ({ ...state, customersLoading: true })),
    on(UserActions.getCustomersSuccess, (state, payload) => ({
        ...state, customers: payload.customers, customerPaging: payload.paging, customersLoading: false
    })),
    on(UserActions.revokeCustomerInvitationSuccess, (state, payload) => {
        const filtered = state.customers.filter(customer => customer.Email !== payload.email);
        return { ...state, customers: filtered };
    }),
    on(UserActions.deleteCustomerSuccess, (state, payload) => {
        const filtered = state.customers.filter(customer => customer.Id !== payload.userId);
        return { ...state, customers: filtered };
    }),
    on(UserActions.readyToSetImpersonation, (state) => ({
        ...state, readyToImpers: true,
    })),
    on(UserActions.endToSetImpersonation, (state) => ({
        ...state, readyToImpers: false,
    })),
    on(UserActions.getCustomersFailed, (state) => ({
        ...state, customersLoading: false,
    })),
    on(UserActions.getAllCustomersSuccess, (state, payload) => ({
        ...state, allCustomers: payload.customers
    })),
    on(UserActions.resetCustomerDialog, (state) => ({ ...state, customerInviting: false, customerInvited: false })),
    on(UserActions.inviteCustomer, (state) => ({ ...state, customerInviting: true, customerInvited: false })),
    on(UserActions.inviteCustomerSuccess, (state) => ({
        ...state, customerInviting: false, customerInvited: true,
    })),
    on(UserActions.inviteCustomerFailed, (state) => ({
        ...state, customerInviting: false, customerInvited: false,
    })),
    on(UserActions.setDraftMode, (state) => ({ ...state, isDraftMode: true })),
    on(UserActions.unsetDraftMode, (state) => ({ ...state, isDraftMode: false })),
    on(UserActions.setImpersonation, (state, payload) => {
        let user: User = payload.user;
        if (!user) {
            user = state.allCustomers.find(c => c.Id === payload.userId);
        }
        if (!user) {
            console.warn(`Trying to impersonate unknown user ${payload.userId}`);
            return { ...state };
        }
        localStorage.setItem(LOCALSTORAGE_USER_DATA, JSON.stringify({ ...state.currentUser, PersonifiedId: user.Id }));
        localStorage.setItem(LOCALSTORAGE_PERSONIFIED_USER, JSON.stringify(user));
        return {
            ...state,
            currentTechnician: { ...state.currentUser, PersonifiedId: user.Id },
            currentUser: { ...user },
            isImpersMode: true,
        };
    }),
    on(UserActions.unsetImpersonation, (state) => {
        localStorage.removeItem(LOCALSTORAGE_USER_DATA);
        localStorage.removeItem(LOCALSTORAGE_PERSONIFIED_USER);
        state.currentTechnician.PersonifiedId = undefined;
        return {
            ...state,
            currentTechnician: null,
            currentUser: state.currentTechnician,
            isImpersMode: false,
            isTechnician: true,
            isUser: false,
        };
    }),
    on(UserActions.resetSearchFailedTechnicianByCode, (state) => ({ ...state, technician: null, searchFailed: false, userTechnicianAssigned: false })),
    on(UserActions.revokeShareInvitationReset, (state) => ({ ...state, deviceShareInvitationRevoked: false })),
    on(UserActions.revokeShareInvitation, (state) => ({ ...state, deviceShareInvitationRevoked: false })),
    on(UserActions.revokeShareInvitationSuccess, (state) => ({ ...state, deviceShareInvitationRevoked: true })),
    on(UserActions.revokeShareInvitationFailed, (state) => ({ ...state, deviceShareInvitationRevoked: false })),
    on(UserActions.closeAccountSuccess, (state) => ({ ...state, accountClosed: true })),
    on(UserActions.updateCurrentUserExitBetaSuccess, (state) => ({ ...state, exitBeta: true })),
    // eslint-disable-next-line max-len
    on(UserActions.getNotificationMaintenanceSuccess, (state, payload) => ({ ...state, notificationMaintenance: payload.notificationMaintenance })),
    on(UserActions.getReleaseNoteList, (state) => ({ ...state, releaseNoteList: [] })),
    on(UserActions.getReleaseNoteListSuccess, (state, payload) => ({ ...state, releaseNoteList: payload.releaseNoteList })),
    on(UserActions.getPrivacyNoteList, (state) => ({ ...state, privacyNoteList: [] })),
    on(UserActions.getPrivacyNoteListSuccess, (state, payload) => ({ ...state, privacyNoteList: payload.privacyNoteList })),
    on(UserActions.setFeReleaseVersionSuccess, (state) => {
        const currentUser = state.currentUser;
        currentUser.ReleaseVersion;
        return {
            ...state,
            currentUser: currentUser,
        };
    }),
    on(UserActions.setFePrivacyVersionSuccess, (state) => {
        const currentUser = state.currentUser;
        currentUser.PrivacyVersion;
        return {
            ...state,
            currentUser: currentUser,
        };
    }),
    on(UserActions.setRatingVote, (state) => ({ ...state, ratingLoading: true, ratingSuccess: false })),
    on(UserActions.setRatingVoteSuccess, (state) => ({ ...state, ratingLoading: false, ratingSuccess: true })),
    on(UserActions.setRatingVoteFailed, (state) => ({ ...state, ratingLoading: false, ratingSuccess: false })),
    on(UserActions.getNextRatingVoteSuccess, (state, payload) => ({ ...state, askNextRatingDate: payload.askNextRatingDate })),
);

export const reducer = (state: State | undefined, action: Action): State => userReducer(state, action);
